import { ChangeDetectionStrategy, Component, OnInit, input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog'
import { ReportService } from 'src/app/services/report.service';
import { ReportForest } from 'src/app/models/reportForest.model';
import { ConfirmationToastComponent } from 'src/app/components/confirmation-toast/confirmation-toast.component';
import { ForestTreament } from 'src/app/models/forestTreatment.model';
import { TreamentSpecification } from 'src/app/models/forestSpecification.model';
import { DialogService } from 'src/app/services/dialog.service';
import { UserService } from 'src/app/services/user.service';
import { faEdit, faExclamationCircle, faWarning, faFileCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { AuthMethodEnum } from 'src/app/models/enum/auth-method.enum';

interface ReportForestExtras {
  data: any[],
  woData: any[],
  form: FormGroup,
}

@Component({
  selector: 'app-ro-auth-funding',
  templateUrl: './ro-auth-funding.component.html',
  styleUrls: ['./ro-auth-funding.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})

export class AuthFundingComponent implements OnInit {
  faEdit = faEdit;
  faExclamationCircle = faExclamationCircle;
  faFileCircleCheck = faFileCircleCheck;

  faWarning = faWarning;
  totalRequested: any = {};

  report = input.required<any>();
  gis_specialist = input.required<any>();
  report_id = input.required<any>();
  treatmentOptions: any;
  user: any;
  authorizationStatusList: any;

  displayedColumns = [
    //'select',
    'treatment',
    'spec_number',
    'spec_name',
    'off_nfs',
    'outside_fire',
    'unit_type',
    'unit_qty',
    'unit_cost',
    'total',
    'authorized_total',
    'authorized_by',
    'authorized_status',
    // 'edit',
  ]
  WO_DisplayedColumns = [
    'treatment',
    'spec_number',
    'spec_name',
    'off_nfs',
    'outside_fire',
    'unit_type',
    'unit_qty',
    'unit_cost',
    'total',
    'authorized_total',
    'authorized_by',
    'authorized_status',
    // 'edit',
  ]
  footerDisplayedColumns = [
    //'select',
    'treatment',
    'total',
    'authorized_total',
    'authorized_by',
  ]
  forestTreatments: any;

  constructor(
    public dialog: MatDialog,
    private readonly formBuilder: FormBuilder,
    public readonly reportService: ReportService,
    public readonly userService: UserService,
    public readonly toast: ConfirmationToastComponent,
    private readonly dialogService: DialogService,
  ) {
  }

  loadEditable() {
    this.displayedColumns = [
      'treatment',
      'spec_number',
      'spec_name',
      'off_nfs',
      'outside_fire',
      'unit_type',
      'unit_qty',
      'unit_cost',
      'total',
      'authorized_total',
      'authorized_by',
      'authorized_status',
    ]
    this.footerDisplayedColumns = [
      'treatment',
      'total',
      'authorized_total',
      'authorized_by',
    ]
  }

  load() {
    this.report().forests.forEach((forest: ReportForest & ReportForestExtras) => {
      forest.form = this.formBuilder.group({
        job_code: [forest.job_code?.trim() ?? "", Validators.maxLength(20)],
        regional_authorization_narrative: [this.customATOB(forest.regional_authorization_narrative ?? ""), Validators.maxLength(6000)],
      })
    });
    this.dialogService.openSpinner();
    this.reportService.getTreatmentOptions().subscribe((options: any) => {
      this.treatmentOptions = options
      this.totalRequested = {}
      this.report().forests.forEach((forest: ReportForest & ReportForestExtras) => {
        forest.data = []
        forest.woData = []
        forest.treatments!.forEach((treatment: ForestTreament) => {
          if (this.totalRequested[forest.region!.toString()] === undefined) {
            this.totalRequested[forest.region!.toString()] = 0
          }
          this.setTreatments(treatment, forest);
        })

        this.totalRequested[forest.region!.toString()] += this.forestTotalAuthorized(forest.data)
      });
      this.dialogService.closeSpinner();
    });
    this.userService.getUserProfile().then((user) => {
      this.user = user;
    })
  }


  getReportForests(): [ReportForest[], boolean] {
    let reportForests: ReportForest[] = []
    let index = 0;
    let allFieldsFilled = true

    this.report().forests.forEach((forest: ReportForest & ReportForestExtras) => {
      let newForest: ReportForest = {
        forest_id: forest.forest_id,
        job_code: forest.form.controls["job_code"].valid ? forest.form.value.job_code : undefined,
        regional_authorization_narrative: forest.form.controls['regional_authorization_narrative'].valid ? this.customBTOA(forest.form.value.regional_authorization_narrative) : undefined,
      }
      reportForests.push(newForest);
      index += 1;
    })

    return [reportForests, allFieldsFilled]
  }

  ngOnInit() {
    this.load()
  }

  setTreatments(treatment: any, forest: any) {
    let options = this.treatmentOptions.find((option: any) => option.treatment_option_id == treatment.treatment_option_id)

    treatment.treatment_specifications!.forEach((spec: TreamentSpecification) => {
      // ro_authorized_by_id 1 is "Forward to WO"
      if (options?.code == "M2" || options?.code == "M3" || spec.ro_authorized_by_id == AuthMethodEnum.FOWARD_TO_WO) { // Determine if the spec needs WO approval
        this.WOApproval(forest, options, spec);
      } else {
        this.noWOApproval(forest, options, spec);
      }
    })

    forest.data.sort((a: any, b: any) => a.spec_number < b.spec_number ? -1 : 1);
    forest.data.sort((a: any, b: any) => a.treatment_option < b.treatment_option ? -1 : 1);
  }

  WOApproval(forest: any, options: any, spec: any) {
    forest.woData.push({
      ...spec,

      rowType: "normal-cell",
      type: options?.treatment_type?.treatment_type,
      treatment: options?.treatment_option,
      region: forest.region,
      unit_type: options?.unit_of_measure,
      select: new FormControl(false),
      unit_qty: spec.custom_unit_qty ?? spec.gis_unit_qty,

    })
    if (spec.wo_authorized_by_id == AuthMethodEnum.WO_AUTHORIZED) {
      if (spec.wo_approved_unit_cost != spec.unit_cost || spec.wo_approved_unit_qty != (spec.custom_unit_qty ?? spec.gis_unit_qty)) {
        forest.woData.push({
          rowType: "bonus-top",
          treatment: "Modified Authorization",
          treatment_option: options?.treatment_option,
          spec_number: spec.specification_number,
        }, {
          rowType: "bonus-data bonus-data-end",
          treatment: "# of Units or Unit Cost Modified",
          treatment_option: options?.treatment_option,
          spec_number: spec.specification_number,
          unit_qty: spec.wo_approved_unit_qty,
          unit_cost: spec.wo_approved_unit_cost,
          qty_class: spec.wo_approved_unit_qty == (spec.custom_unit_qty ?? spec.gis_unit_qty) ? undefined : 'yellow',
          cost_class: spec.wo_approved_unit_cost == spec.unit_cost ? undefined : 'yellow',
          hideRequested: true,
          wo_approved_unit_qty: spec.wo_approved_unit_qty,
          wo_approved_unit_cost: spec.wo_approved_unit_cost,
        })
      }
    }
  }

  noWOApproval(forest: any, options: any, spec: any) {
    let mainEntry = {
      region: forest.region,
      rowType: "normal-cell",
      type: options?.treatment_type?.treatment_type,
      treatment: options?.treatment_option,
      treatment_option: options?.treatment_option,
      unit_type: options?.unit_of_measure,
      unit_qty: spec.custom_unit_qty ?? spec.gis_unit_qty,
      select: new FormControl(false),
      ...spec
    }
    forest.data.push(mainEntry)
    if (spec.ro_authorized_by_id == AuthMethodEnum.SPLIT_AUTHORIZATION) {
      this.splitAuth(options, spec, mainEntry, forest);
    }
    if (spec.ro_authorized_by_id == AuthMethodEnum.RO_AUTHORIZED) {
      if (spec.approved_unit_cost != spec.unit_cost || spec.approved_unit_qty != (spec.custom_unit_qty ?? spec.gis_unit_qty)) {
        forest.data.push({
          rowType: "bonus-top",
          treatment: "Modified Authorization",
          treatment_option: options?.treatment_option,
          spec_number: spec.specification_number,
        }, {
          rowType: "bonus-data",
          treatment: "# of Units or Unit Cost Modified",
          treatment_option: options?.treatment_option,
          spec_number: spec.specification_number,
          unit_qty: spec.approved_unit_qty,
          unit_cost: spec.approved_unit_cost,
          qty_class: spec.approved_unit_qty == (spec.custom_unit_qty ?? spec.gis_unit_qty) ? undefined : 'yellow',
          cost_class: spec.approved_unit_cost == spec.unit_cost ? undefined : 'yellow',
          hideRequested: true,
          approved_unit_qty: spec.approved_unit_qty,
          approved_unit_cost: spec.approved_unit_cost,
        })
      }
    }
  }

  splitAuth(options: any, spec: any, mainEntry: any, forest: any) {
    let items = [
      {
        rowType: "bonus-top",
        treatment: "Split Authorization",
        treatment_option: options?.treatment_option,
        spec_number: spec.specification_number,
      },
      {
        rowType: "bonus-data",
        treatment: "RO Split Authorization",
        treatment_option: options?.treatment_option,
        spec_number: spec.specification_number,
        unit_qty: spec.custom_unit_qty ?? spec.gis_unit_qty,
        unit_cost: spec.unit_cost,
        approved_unit_qty: spec.approved_unit_qty,
        approved_unit_cost: spec.approved_unit_cost,
        cost_class: spec.approved_unit_cost == spec.unit_cost ? undefined : 'yellow',
      },
      {
        rowType: "bonus-data bonus-data-end",
        treatment: "WO Split Authorization",
        treatment_option: options?.treatment_option,
        spec_number: spec.specification_number,
        unit_qty: spec.custom_unit_qty ?? spec.gis_unit_qty,
        unit_cost: spec.unit_cost,

        // show wo approved unit qty, but if not approved yet, show difference in requested - re approved
        wo_approved_unit_qty: spec.wo_approved_unit_qty || ((spec.custom_unit_qty ?? spec.gis_unit_qty) - spec.approved_unit_qty),
        wo_approved_unit_cost: spec.wo_approved_unit_cost,

        // highlight yellow differences between expected and approved
        qty_class: spec.wo_approved_unit_qty && spec.wo_approved_unit_qty != ((spec.custom_unit_qty ?? spec.gis_unit_qty) - spec.approved_unit_qty) ? 'yellow' : undefined,
        cost_class: spec.wo_approved_unit_cost && spec.wo_approved_unit_cost != spec.unit_cost ? 'yellow' : undefined,
      }
    ]
    let woEntry = { ...mainEntry }
    woEntry.select = new FormControl(false)
    woEntry.zeroRequested = true // hide requested funds on WO
    forest.data.push(...items)
    forest.woData.push(woEntry, ...items)
  }

  unitTotal(item: any, allow?: boolean) {
    if (!allow && item.rowType != "normal-cell") return 0
    if (item.zeroRequested) return 0
    return item.unit_cost * item.unit_qty;
  }
  unitTotalAuthorized(item: any) {
    if (item.rowType != "normal-cell") return 0
    return item.approved_unit_qty * item.approved_unit_cost;
  }

  forestTotal(data: any[]) {
    let total = 0;
    if (data == null) return 0
    for (let item of data) {
      total += this.unitTotal(item) || 0; // ?? Does not cover NaN
    }
    return total;
  }

  forestTotalAuthorized(data: any[]) {
    let total = 0;
    if (data == null) return 0
    for (let item of data) {
      total += this.unitTotalAuthorized(item) || 0; // ?? Does not cover NaN
    }
    return total;
  }

  getAuthStatusText(id: any) {
    if (this.authorizationStatusList == null) return
    return this.authorizationStatusList[id].text
  }

  customATOB(s: string): string {
    try {
      return atob(s)
    } catch (err) {
      if (err) {
        return s
      }
    }
    return s
  }

  customBTOA(s: string): string {
    return btoa(s.replaceAll(/[\u00A0-\u2666]/g, function (c) {
      return '&#' + c.codePointAt(0) + ';';
    }));
  }

  tooltip(item: any) {
    let text = ""
    for (let id of item.authorization_status_ids ?? []) {
      if (text != "") text += "\n\n"
      text += this.getAuthStatusText(id)
    }
    if (text != "") text += "\n\n"
    text += ((item.authorization_note) ?? '')
    return text
  }

  notify_gis_specialist() {
    this.reportService.notifyGIS(this.report_id()).subscribe((resp: any) => {
      this.toast.openSuccessSnackBar(`An email notification has been sent to the assigned GIS Specialist.`);
    })
  }
}